/* Product cards section: hovering (or focusing) a color swatch previews that
   variant's image in the card; leaving restores the featured image. Swatches
   remain plain links to the variant, so touch devices simply navigate. */
if (!customElements.get('product-card-simple')) {
  customElements.define(
    'product-card-simple',
    class ProductCardSimple extends HTMLElement {
      connectedCallback() {
        this.swatches = Array.from(this.querySelectorAll('.swatches li[data-media-id]'));
        if (!this.swatches.length) return;

        this.variantMedia = Array.from(this.querySelectorAll('.product-card__media--variant'));
        this.hoverable = window.matchMedia('(hover: hover)');

        this.onEnter = this.onEnter.bind(this);
        this.onLeave = this.onLeave.bind(this);

        this.swatches.forEach((li) => {
          li.addEventListener('mouseenter', this.onEnter);
          li.addEventListener('mouseleave', this.onLeave);
          li.addEventListener('focusin', this.onEnter);
          li.addEventListener('focusout', this.onLeave);
        });
      }

      disconnectedCallback() {
        if (!this.swatches) return;
        this.swatches.forEach((li) => {
          li.removeEventListener('mouseenter', this.onEnter);
          li.removeEventListener('mouseleave', this.onLeave);
          li.removeEventListener('focusin', this.onEnter);
          li.removeEventListener('focusout', this.onLeave);
        });
      }

      onEnter(event) {
        if (event.type === 'mouseenter' && !this.hoverable.matches) return;
        const mediaId = event.currentTarget.dataset.mediaId;
        this.showMedia(mediaId);
      }

      onLeave() {
        this.showMedia(null);
      }

      showMedia(mediaId) {
        let matched = false;
        this.variantMedia.forEach((el) => {
          const active = mediaId !== null && el.dataset.mediaId === mediaId;
          el.classList.toggle('is-active', active);
          if (active) matched = true;
        });
        // If the hovered swatch maps to the featured image (no variant layer
        // rendered for it), fall back to showing the featured image.
        this.classList.toggle('is-previewing', matched);
      }
    }
  );
}

/* Product cards grid / carousel wrapper. In carousel mode it turns the f-grid
   into a Swiper (1 card on mobile, up to the tablet/desktop column counts),
   wiring the optional arrows and dots rendered by the section. */
if (!customElements.get('product-cards-grid')) {
  customElements.define(
    'product-cards-grid',
    class ProductCardsGrid extends HTMLElement {
      connectedCallback() {
        this.layout = this.dataset.layout || 'grid';
        if (this.layout !== 'carousel') return;

        this.sectionId = this.dataset.sectionId;
        this.section = this.closest(`.section-${this.sectionId}`) || this;
        this.slider = this.querySelector('.product-cards-grid__slider');
        this.wrapper = this.querySelector('.product-cards');
        this.items = parseInt(this.dataset.items) || 4;
        this.tabletItems = parseInt(this.dataset.tabletItems) || 2;
        this.sliderInstance = false;

        if (!this.slider || !this.wrapper || typeof window.FoxTheme === 'undefined' || !FoxTheme.Carousel) return;

        this.initSlider();
      }

      disconnectedCallback() {
        if (typeof this.sliderInstance === 'object' && this.sliderInstance.slider) {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initSlider() {
        if (typeof this.sliderInstance === 'object') return;

        const slides = this.wrapper.querySelectorAll('.swiper-slide');
        if (slides.length === 0) return;

        const gap = FoxTheme.utils && FoxTheme.utils.getGridColumnGap
          ? FoxTheme.utils.getGridColumnGap(this.wrapper)
          : { mobile: 16, tablet: 24, tabletLarge: 24, desktop: 30 };

        const prevEl = this.querySelector('.swiper-button-prev');
        const nextEl = this.querySelector('.swiper-button-next');
        const paginationEl = this.querySelector('.swiper-pagination');

        this.sliderOptions = {
          slidesPerView: 1,
          spaceBetween: gap.mobile,
          navigation: prevEl && nextEl ? { nextEl, prevEl } : false,
          pagination: paginationEl ? { el: paginationEl, clickable: true } : false,
          breakpoints: {
            768: {
              slidesPerView: Math.min(this.tabletItems, slides.length),
              spaceBetween: gap.tablet,
            },
            1024: {
              slidesPerView: Math.min(this.items, slides.length),
              spaceBetween: gap.desktop,
            },
          },
          loop: slides.length > this.items,
          threshold: 2,
          watchOverflow: true,
        };

        // Hand the grid over to Swiper.
        this.wrapper.classList.remove('f-grid');
        this.wrapper.classList.add('swiper-wrapper');

        this.sliderInstance = new FoxTheme.Carousel(this.slider, this.sliderOptions);
        this.sliderInstance.init();

        this.calcNavButtonsPosition();
        window.addEventListener(
          'resize',
          FoxTheme.utils && FoxTheme.utils.debounce
            ? FoxTheme.utils.debounce(() => this.calcNavButtonsPosition(), 100)
            : () => this.calcNavButtonsPosition()
        );
      }

      // Center the arrows on the product image rather than the whole card.
      calcNavButtonsPosition() {
        const firstMedia = this.querySelector('.product-card__image-wrapper');
        if (firstMedia && firstMedia.clientHeight > 0) {
          this.style.setProperty('--swiper-navigation-top-offset', parseInt(firstMedia.clientHeight) / 2 + 'px');
        }
      }
    }
  );
}
